import { createSelector } from '@reduxjs/toolkit';
import { selectLogLocalDataByFilename } from '../log/selectors';
import { AppState } from '../types';
import { BleConnectionState } from './constants';
import { selectAuthUserInfo, selectUserFeatureFlags } from '../auth/selectors';
import { BleControllerDevice } from './types';
import { AuthUserInfoController } from '../auth/types';
import { Roles } from '@dataquiver/stoneage-roles';

const selectBle = (state: AppState) => state.ble;

export const selectBleConnectionState = createSelector(selectBle, state => state.connectionState);

/** A selector that returns whether or not the application is currently trying to connect to the BLE device. */
export const selectBleIsConnectingDevice = createSelector(
  selectBleConnectionState,
  state => state === BleConnectionState.ATTEMPTING_CONNECT,
);

export interface BleConnectableDeviceListItem
  extends BleControllerDevice,
    Pick<AuthUserInfoController, 'description' | 'physicalSerial'> {}

export const selectBleConnectableDevices = createSelector(selectBle, selectAuthUserInfo, (state, user) => {
  if (!user?.controllers) return [];

  return (
    state.connectableDevices
      .map(i => {
        const controllerInfo = user.controllers.find(c => c.ssn === i.serialNumber);
        const result: BleConnectableDeviceListItem = {
          ...i,
          description: controllerInfo?.description,
          physicalSerial: controllerInfo?.physicalSerial,
        };
        return result;
      })
      // StoneAge Admin and Dev can connect to any controller.
      .filter(i => (user.role >= Roles.roles.STONEAGE_ADMIN ? true : !!i.physicalSerial))
  );
});

export const selectBleConnectedDevice = createSelector(selectBle, selectAuthUserInfo, (state, user) => {
  if (!state.connectedDevice) return undefined;
  const controllerInfo = user?.controllers?.find(c => c.ssn === state.connectedDevice?.serialNumber);
  return {
    ...state.connectedDevice,
    description: controllerInfo?.description,
    physicalSerial: controllerInfo?.physicalSerial,
    isControllerInOrg: !!user?.org && controllerInfo?.org === user?.org,
  };
});

export const selectBleFirmwareVersion = createSelector(selectBleConnectedDevice, dev => dev?.firmwareVersion);

export const selectBleSerialNumber = createSelector(selectBleConnectedDevice, dev => dev?.serialNumber);

export const selectBleIsLocked = createSelector(selectBle, state => state.isLocked);

export const selectBleRemoteLogFiles = createSelector(selectBle, state => state.remoteLogFiles);

export const selectBleLastConnectedDevice = createSelector(selectBle, state => state.lastConnectedDevice);

export const selectBleWriteLog = createSelector(selectBle, state => state.writeLog);

export const selectBleReadLog = createSelector(selectBle, state => state.readLog);

export const selectBleUpdateFirmware = createSelector(selectBle, state => state.updateFirmware);

export const selectBleUpdateFirmwareStatus = createSelector(selectBle, state => state.updateFirmwareStatus);

export const selectBleSyncLog = createSelector(selectBle, state => state.syncLog);

export const selectBleSyncStatus = createSelector(selectBleSyncLog, selectLogLocalDataByFilename, (sync, log) => {
  if (!sync) return undefined;
  const status: 'transfer' | 'upload' | 'success' | 'error' = !!log[sync.filename]
    ? 'upload'
    : sync.hasErrors === true
      ? 'error'
      : sync.hasErrors === false
        ? 'success'
        : 'transfer';
  return {
    status,
    progress: status === 'transfer' || status === 'upload' ? sync.syncedBytes / sync.totalBytes : undefined,
    filename: sync.filename,
  };
});

/** A selector that returns a list of the bundle files on the controller device. */
export const selectBleBundleFiles = createSelector(selectBle, state => state.remoteBundleFiles);

/** A selector that returns information about the bundle file currently being written. */
export const selectBleWriteBundle = createSelector(selectBle, state => state.writeBundle);

/** A selector that returns a function which checks if the user can live stream from the provided controller. */
export const selectBleCanLiveStreamFromController = createSelector(
  selectUserFeatureFlags,
  selectAuthUserInfo,
  (featureFlags, userInfo) => {
    return (serialNumber: string) => {
      if (!userInfo) return false; // If we don't know who the user is, they can't live stream.
      if (!featureFlags['LOGS_LIVE_STREAM']) return false; // If the user doesn't have the live stream feature enabled, they cannot live stream no matter what.
      if (Roles.isStoneAgeDev(userInfo.role)) return true; // Stoneage Devs can always live stream, regardless of controller.

      const controller = userInfo.controllers.find(i => i.ssn === serialNumber);
      if (!controller) return false; // If this controller wasn't included in the list of controllers they have access to, they should not have even been able to connect to it! Regardless, they can't stream from it either.
      return controller.org === userInfo.org; // Users can only start streaming from controllers in their own org.
    };
  },
);

/** A selector that returns whether or not the user has been prompted to create the currently connected controller. Only applies to SA Devs connecting a new controller for the first time. */
export const selectBleDidPromptCreateController = createSelector(selectBle, state => state.didPromptCreateController);

/** A selector that returns whether or not the "Connect Controller" modal is open. */
export const selectBleIsConnectControllerModalOpen = createSelector(
  selectBle,
  state => state.isConnectControllerModalOpen,
);
